import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { WorkspaceSwitcher } from "@header/WorkspaceSwitcher";
import {
  LayoutDashboard,
  FileText,
  Library,
  Menu,
  X,
  Sparkles,
  Lock,
  FlaskConical,
  AlertTriangle,
  GraduationCap,
  BookMarked,
  Scale,
  ClipboardList,
  ScanSearch,
  ShieldCheck,
  BookText,
  BrainCircuit,
  Users,
  Store,
  Zap,
} from "lucide-react";
import { cn } from "@foundation/lib/utils";
import { getActiveWorkspaceId } from "../../../header/activeWorkspace";
import { useToast } from "@foundation/hooks/use-toast";
import { usePolicyApproval } from "../hooks/usePolicyApproval";
import { supabase } from "@foundation/utils/supabaseClient";

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || "http://localhost:3001";

type NavItem = {
  name: string;
  href: string;
  icon: React.ElementType;
};

const navigationItems: NavItem[] = [
  { name: "Create Your First Policy", href: "/onboarding/boost-policy", icon: Sparkles },
  { name: "Dashboard", href: "/revisr/dashboard", icon: LayoutDashboard },
  { name: "Your Policies", href: "/revisr/policies", icon: FileText },
  { name: "Revisr Library", href: "/revisr/resource-library/documents", icon: Library },
  { name: "Org Memory", href: "/revisr/org-memory", icon: BrainCircuit },
];

type UsageResult = {
  used: number;
  total: number;
  canCreate: boolean;
  percentage: number;
  planName: string;
  tierLevel: number;
  hasLimit: boolean;
};

const DEFAULT_USAGE: UsageResult = {
  used: 0, total: 0, canCreate: true, percentage: 0,
  planName: '', tierLevel: 0, hasLimit: false,
};

function usePolicyUsage(): UsageResult {
  const [usage, setUsage] = useState<UsageResult>(DEFAULT_USAGE);

  const fetchUsage = useCallback(async () => {
    const orgId = getActiveWorkspaceId();
    if (!orgId) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/revisr/subscription/info`, {
        headers: {
          'Content-Type': 'application/json',
          'x-org-id': orgId,
        },
      });

      if (!res.ok) return;

      const data = await res.json();
      const policies = data?.usage?.policies;

      if (!policies) {
        setUsage(DEFAULT_USAGE);
        return;
      }

      const used: number = policies.current ?? 0;
      const total: number = policies.limit ?? -1;
      const hasLimit = total > 0;
      const plan = data?.subscription?.plan;
      const planName: string = plan?.name ?? 'Basic';
      const tierLevel: number = plan?.tier_level ?? 0;

      setUsage({
        used,
        total: hasLimit ? total : 0,
        canCreate: hasLimit ? used < total : true,
        percentage: hasLimit && total > 0 ? Math.round((used / total) * 100) : 0,
        planName,
        tierLevel,
        hasLimit,
      });
    } catch (err) {
      console.error('[usePolicyUsage] fetch error:', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 10;
    const RETRY_DELAY_MS = 400;

    const tryFetch = () => {
      if (cancelled) return;
      const orgId = getActiveWorkspaceId();
      if (orgId) { fetchUsage(); return; }
      if (attempts < MAX_ATTEMPTS) { attempts++; setTimeout(tryFetch, RETRY_DELAY_MS); }
    };

    tryFetch();
    return () => { cancelled = true; };
  }, [fetchUsage]);

  useEffect(() => {
    const handle = () => fetchUsage();
    window.addEventListener('workspace-changed', handle);
    window.addEventListener('policy-finalized', handle);
    return () => {
      window.removeEventListener('workspace-changed', handle);
      window.removeEventListener('policy-finalized', handle);
    };
  }, [fetchUsage]);

  return usage;
}

type ProLockedItemProps = {
  name: string;
  icon: React.ElementType;
  sidebarCollapsed: boolean;
  standalone?: boolean;
};

function ProLockedItem({ name, icon: Icon, sidebarCollapsed, standalone = false }: ProLockedItemProps) {
  if (standalone) {
    if (sidebarCollapsed) {
      return <div className="h-px mx-2 my-1" style={{ background: "rgba(252,251,244,0.08)" }} />;
    }
    return (
      <div className="px-3 pt-3 pb-1 flex items-center gap-2 cursor-not-allowed select-none">
        <p
          className="text-[9px] font-bold uppercase tracking-[0.12em]"
          style={{ color: "rgba(252,251,244,0.3)" }}
        >
          {name}
        </p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center rounded-lg text-sm font-medium cursor-not-allowed select-none",
        sidebarCollapsed ? "justify-center px-0 py-2.5" : "px-3 py-2 gap-2.5"
      )}
      title={sidebarCollapsed ? name : "Available on Pro plan"}
    >
      <Icon
        className={cn("flex-shrink-0", sidebarCollapsed ? "h-5 w-5" : "h-4 w-4")}
        style={{ color: "rgba(252,251,244,0.18)" }}
      />
      {!sidebarCollapsed && (
        <span className="flex-1 truncate" style={{ color: "rgba(252,251,244,0.22)" }}>
          {name}
        </span>
      )}
      {!sidebarCollapsed && (
        <Lock className="h-3 w-3 flex-shrink-0" style={{ color: "rgba(252,251,244,0.18)" }} />
      )}
    </div>
  );
}

type ScopingAISidebarProps = {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
};

const EDITOR_PATH_PREFIXES = [
  "/revisr/create-policy/edit",
  "/revisr/create-policy/generated",
  "/revisr/brand-policy/editor",
  "/revisr/brand-policy/preview",
  "/revisr/brand-policy/generating",
  "/revisr/edit-approve",
  "/revisr/preview-export",
  "/revisr/policies/generated",
  "/revisr/template-editor/",
  "/revisr/scan-review",
];

export default function ScopingAISidebar({ mobileOpen = false, onMobileClose }: ScopingAISidebarProps) {
  const location = useLocation();
  const { toast } = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const isEditorPage = EDITOR_PATH_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix)
  );
  const effectiveCollapsed = sidebarCollapsed || isEditorPage;
  const usage = usePolicyUsage();
  // Approval workflow: show the Policy Approvals entry only to org admins
  // and only when the VITE_POLICY_APPROVAL_REQUIRED flag is on.
  const { approvalRequired, isOrgAdmin } = usePolicyApproval();
  const showPolicyApprovals = approvalRequired && isOrgAdmin;

  // Track active workspace name/type for the header — seeded from localStorage, updated on switch
  const [activeOrgName, setActiveOrgName] = useState<string>(() => {
    try {
      const raw = localStorage.getItem('revisr_active_workspace_meta');
      return raw ? JSON.parse(raw).name : 'Organisation';
    } catch { return 'Organisation'; }
  });
  const [activeOrgType, setActiveOrgType] = useState<string>(() => {
    try {
      const raw = localStorage.getItem('revisr_active_workspace_meta');
      return raw ? JSON.parse(raw).type : 'organisation';
    } catch { return 'organisation'; }
  });

  useEffect(() => {
    const handler = (e: Event) => {
      const ws = (e as CustomEvent<{ name: string; tenant_type: string }>).detail;
      if (!ws?.name) return;
      setActiveOrgName(ws.name);
      setActiveOrgType(ws.tenant_type === 'personal' ? 'personal' : 'organisation');
      localStorage.setItem('revisr_active_workspace_meta', JSON.stringify({ name: ws.name, type: ws.tenant_type }));
    };
    window.addEventListener('workspace-changed', handler);
    return () => window.removeEventListener('workspace-changed', handler);
  }, []);
  // null = still loading, true/false = known. Seed from sessionStorage so
  // there's no flash on re-renders within the same session.
  const CACHE_KEY = 'revisr_sample_policy_completed';
  const cached = sessionStorage.getItem(CACHE_KEY);
  const [samplePolicyCompleted, setSamplePolicyCompleted] = useState<boolean | null>(
    cached === 'true' ? true : cached === 'false' ? false : null
  );

  useEffect(() => {
    const check = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .schema('altzero')
        .from('base_profiles')
        .select('sample_policy_completed')
        .eq('id', user.id)
        .single();
      const completed = data?.sample_policy_completed === true;
      sessionStorage.setItem(CACHE_KEY, String(completed));
      setSamplePolicyCompleted(completed);
    };
    check();
  }, []);

  const isActive = (href: string) => {
    if (href === "/revisr/dashboard") {
      return location.pathname === "/revisr" || location.pathname === "/revisr/dashboard";
    }
    if (href === "/revisr/boost-policy") {
      return (
        location.pathname === "/revisr/boost-policy" ||
        location.pathname === "/revisr/audit-workflow-selection" ||
        location.pathname.startsWith("/revisr/create-policy") ||
        location.pathname.startsWith("/revisr/new-audit") ||
        location.pathname.startsWith("/revisr/brand-policy")
      );
    }
    if (href === "/revisr/resource-library/documents") {
      return location.pathname.startsWith("/revisr/resource-library/documents");
    }
    return location.pathname === href || location.pathname.startsWith(href + "/");
  };

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    if (href === "/revisr/boost-policy" && usage.hasLimit && !usage.canCreate) {
      e.preventDefault();
      toast({
        title: "Policy limit reached",
        description: `You've used ${usage.used}/${usage.total} policies. Upgrade your plan to create more.`,
        variant: "destructive",
      });
      return;
    }
    onMobileClose?.();
  };

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden border-r border-[#33b864]/15",
        // Mobile: fixed drawer sliding in from left
        "fixed top-14 bottom-0 left-0 z-50 transition-transform duration-300 ease-in-out",
        mobileOpen ? "translate-x-0" : "-translate-x-full",
        // Desktop: static in layout, no transform
        "md:relative md:top-auto md:bottom-auto md:left-auto md:z-auto md:translate-x-0",
        "md:transition-all md:duration-300",
        // Mobile: always full-width drawer; desktop: collapsible
        "w-64 md:transition-none",
        effectiveCollapsed ? "md:w-16" : "md:w-64"
      )}
      style={{ background: "#132321" }}
      data-testid="sidebar"
    >
      {/* Subtle background texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)`,
          backgroundSize: "24px 24px",
        }}
      />

      {/* Top green accent line */}
      <div className="h-[2px] w-full flex-shrink-0" style={{ background: "linear-gradient(90deg, #33b864, transparent)" }} />

      {/* Workspace Switcher */}
      {!effectiveCollapsed && (
        <div className="pt-3 px-2 relative z-10">
          <WorkspaceSwitcher
            onWorkspaceChange={(ws: { name: string; tenant_type: string }) => {
              setActiveOrgName(ws.name);
              setActiveOrgType(ws.tenant_type === 'personal' ? 'personal' : 'organisation');
              localStorage.setItem('revisr_active_workspace_meta', JSON.stringify({ name: ws.name, type: ws.tenant_type }));
            }}
          />
        </div>
      )}

      {/* Header */}
      <div
        className={cn(
          "px-4 pb-4 pt-3 border-b border-[#ffffff]/8 relative z-10",
          effectiveCollapsed && "px-2"
        )}
      >
        <div className="flex items-center justify-between">
          <Link to="/revisr/dashboard" className="flex items-center gap-2 min-w-0" onClick={() => onMobileClose?.()}>
            {!effectiveCollapsed && (
              <div className="min-w-0">
                <h1
                  className="text-base font-bold leading-tight truncate"
                  style={{ color: "#ffffff", letterSpacing: "-0.01em" }}
                >
                  {activeOrgName}
                </h1>
                <p className="text-[11px] leading-tight mt-0.5 truncate" style={{ color: "#ffffff", opacity: 0.5 }}>
                  {activeOrgType === 'personal' ? 'Personal Workspace' : 'Organisation'}
                </p>
              </div>
            )}
          </Link>
          {/* Desktop: collapse toggle */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden md:flex p-1.5 rounded-lg transition-colors flex-shrink-0"
            style={{ background: "transparent" }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = "rgba(252,251,244,0.08)")}
            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = "transparent")}
            title={effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <Menu className="h-4 w-4" style={{ color: "#ffffff", opacity: 0.6 }} />
          </button>
          {/* Mobile: close drawer button */}
          <button
            onClick={() => onMobileClose?.()}
            className="md:hidden p-1.5 rounded-lg transition-colors flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
            style={{ background: "transparent" }}
            aria-label="Close navigation"
          >
            <X className="h-5 w-5" style={{ color: "#ffffff", opacity: 0.7 } as React.CSSProperties} />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-3 overflow-y-auto scrollbar-hidden space-y-0.5 relative z-10">

        {/* Primary nav items */}
        {navigationItems.filter((item) =>
          !(item.href === "/onboarding/boost-policy" && samplePolicyCompleted !== false)
        ).map((item) => {
          const isLimitedItem = item.href === "/revisr/boost-policy";
          const isBlocked = isLimitedItem && usage.hasLimit && !usage.canCreate;
          const active = isActive(item.href);
          const isSample = item.href === "/onboarding/boost-policy";

          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className={cn(
                "flex items-center rounded-lg text-sm font-medium transition-all duration-150 group relative",
                effectiveCollapsed ? "justify-center px-0 py-2.5" : "px-3 py-2 gap-2.5",
                isBlocked && "cursor-not-allowed opacity-50"
              )}
              style={{
                background: isSample
                  ? active ? "rgba(249,115,22,0.2)" : "rgba(249,115,22,0.12)"
                  : active ? "rgba(51,184,100,0.14)" : "transparent",
                color: isSample ? "#f97316" : active ? "#33b864" : isBlocked ? "#ef4444" : "rgba(255,255,255,0.7)",
                border: isSample ? "1px solid rgba(249,115,22,0.25)" : "1px solid transparent",
              }}
              onMouseEnter={(e) => {
                if (!isBlocked) {
                  (e.currentTarget as HTMLElement).style.background = isSample ? "rgba(249,115,22,0.28)" : active ? "rgba(51,184,100,0.14)" : "rgba(255,255,255,0.06)";
                  (e.currentTarget as HTMLElement).style.color = isSample ? "#f97316" : active ? "#33b864" : "#ffffff";
                }
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = isSample
                  ? active ? "rgba(249,115,22,0.2)" : "rgba(249,115,22,0.12)"
                  : active ? "rgba(51,184,100,0.14)" : "transparent";
                (e.currentTarget as HTMLElement).style.color = isSample ? "#f97316" : active ? "#33b864" : isBlocked ? "#ef4444" : "rgba(255,255,255,0.7)";
              }}
              title={effectiveCollapsed ? item.name : isBlocked ? "Policy limit reached" : undefined}
            >
              {active && !effectiveCollapsed && (
                <div
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full"
                  style={{ background: isSample ? "#f97316" : "#33b864" }}
                />
              )}
              <item.icon
                className={cn("flex-shrink-0", effectiveCollapsed ? "h-5 w-5" : "h-4 w-4")}
                style={{ color: isSample ? "#f97316" : active ? "#33b864" : isBlocked ? "#ef4444" : "rgba(255,255,255,0.45)" }}
              />
              {!effectiveCollapsed && <span className="flex-1 truncate">{item.name}</span>}
            </Link>
          );
        })}

        {/* Policy Approvals - org admins only, shown when the approval flag is on */}
        {showPolicyApprovals && (
          <Link
            to="/revisr/policy-approvals"
            onClick={() => onMobileClose && onMobileClose()}
            className={cn(
              "flex items-center rounded-lg text-sm font-medium transition-all duration-150 group relative",
              effectiveCollapsed ? "justify-center px-0 py-2.5" : "px-3 py-2 gap-2.5"
            )}
            style={{
              background: isActive("/revisr/policy-approvals") ? "rgba(51,184,100,0.14)" : "transparent",
              color: isActive("/revisr/policy-approvals") ? "#33b864" : "rgba(255,255,255,0.7)",
              border: "1px solid transparent",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = isActive("/revisr/policy-approvals") ? "rgba(51,184,100,0.14)" : "rgba(255,255,255,0.06)";
              (e.currentTarget as HTMLElement).style.color = "#ffffff";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = isActive("/revisr/policy-approvals") ? "rgba(51,184,100,0.14)" : "transparent";
              (e.currentTarget as HTMLElement).style.color = isActive("/revisr/policy-approvals") ? "#33b864" : "rgba(255,255,255,0.7)";
            }}
            title={effectiveCollapsed ? "Policy Approvals" : undefined}
          >
            {isActive("/revisr/policy-approvals") && !effectiveCollapsed && (
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full"
                style={{ background: "#33b864" }}
              />
            )}
            <ShieldCheck
              className={cn("flex-shrink-0", effectiveCollapsed ? "h-5 w-5" : "h-4 w-4")}
              style={{ color: isActive("/revisr/policy-approvals") ? "#33b864" : "rgba(255,255,255,0.45)" }}
            />
            {!effectiveCollapsed && <span className="flex-1 truncate">Policy Approvals</span>}
          </Link>
        )}

        {/* Pro section divider */}
        <div className={cn("pt-4 pb-1", effectiveCollapsed ? "px-2" : "px-1")}>
          <div style={{ height: "1px", background: "rgba(255,255,255,0.07)" }} />
        </div>

        {/* Pro section header */}
        {!effectiveCollapsed && (
          <div className="flex items-center gap-2 px-3 pb-2">
            <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.25)" }}>
              Pro Features
            </span>
            <span
              className="text-[9px] font-bold px-1.5 py-px rounded-sm leading-none"
              style={{ background: "rgba(251,191,36,0.12)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.2)" }}
            >
              PRO
            </span>
          </div>
        )}

        {/* Registers group */}
        {!effectiveCollapsed && (
          <div className="px-3 pb-1 pt-1">
            <span className="text-[9px] font-semibold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.2)" }}>
              Registers
            </span>
          </div>
        )}
        {effectiveCollapsed && <div className="h-px mx-2 mb-1" style={{ background: "rgba(255,255,255,0.06)" }} />}
        <div className="space-y-0.5">
          {/* Document Register — unlocked */}
          <Link
            to="/revisr/document-register"
            className={cn(
              "flex items-center rounded-lg text-sm font-medium transition-colors hover:bg-white/10",
              effectiveCollapsed ? "justify-center px-0 py-2.5" : "px-3 py-2 gap-2.5"
            )}
            title={effectiveCollapsed ? "Document Register" : undefined}
            style={{ color: "rgba(255,255,255,0.7)" }}
          >
            <ClipboardList
              className={cn("flex-shrink-0", effectiveCollapsed ? "h-5 w-5" : "h-4 w-4")}
              style={{ color: "rgba(255,255,255,0.7)" }}
            />
            {!effectiveCollapsed && (
              <>
                <span className="flex-1 truncate">Document Register</span>
                <span
                  className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
                  style={{ background: "rgba(51,184,100,0.15)", color: "#33b864" }}
                >
                  Soon
                </span>
              </>
            )}
          </Link>
          {[
            { name: "Risk Register", icon: AlertTriangle },
            { name: "SDS Register", icon: FlaskConical },
            { name: "Training Register", icon: GraduationCap },
            { name: "OFI Register", icon: BookMarked },
          ].map((item) => (
            <ProLockedItem key={item.name} name={item.name} icon={item.icon} sidebarCollapsed={effectiveCollapsed} />
          ))}
        </div>

        {/* Audit Tools group */}
        {!effectiveCollapsed && (
          <div className="px-3 pb-1 pt-3">
            <span className="text-[9px] font-semibold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.2)" }}>
              Audit Tools
            </span>
          </div>
        )}
        {effectiveCollapsed && <div className="h-px mx-2 my-1" style={{ background: "rgba(255,255,255,0.06)" }} />}
        <div className="space-y-0.5">
          {[
            { name: "Gap Analysis", icon: ScanSearch },
            { name: "Compliance Intelligence", icon: ShieldCheck },
          ].map((item) => (
            <ProLockedItem key={item.name} name={item.name} icon={item.icon} sidebarCollapsed={effectiveCollapsed} />
          ))}
        </div>

        {/* More Pro items */}
        {!effectiveCollapsed && (
          <div className="px-3 pb-1 pt-3">
            <span className="text-[9px] font-semibold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.2)" }}>
              More
            </span>
          </div>
        )}
        {effectiveCollapsed && <div className="h-px mx-2 my-1" style={{ background: "rgba(255,255,255,0.06)" }} />}
        <div className="space-y-0.5">
          {[
            { name: "SOPs / Procedures", icon: ClipboardList },
            { name: "Work Instructions", icon: BookText },
            { name: "AI Governance", icon: BrainCircuit },
            { name: "Clients / CRM", icon: Users },
            { name: "Marketplace", icon: Store },
          ].map((item) => (
            <ProLockedItem key={item.name} name={item.name} icon={item.icon} sidebarCollapsed={effectiveCollapsed} />
          ))}
        </div>
      </nav>

      {/* Usage widget */}
      {!effectiveCollapsed && <SidebarUsageWidget usage={usage} />}
    </div>
  );
}

type UsageData = ReturnType<typeof usePolicyUsage>;

function SidebarUsageWidget({ usage }: { usage: UsageData }) {
  if (!usage.hasLimit) return null;

  const { used, total, canCreate, percentage, planName, tierLevel } = usage;

  const barColor = !canCreate ? "#ef4444" : percentage >= 75 ? "#f59e0b" : "#33b864";

  return (
    <div
      className="px-3 py-3 relative z-10"
      style={{ borderTop: "1px solid rgba(252,251,244,0.08)" }}
    >
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium" style={{ color: "rgba(252,251,244,0.6)" }}>
            {planName}
          </span>
          {tierLevel === 0 && (
            <Link
              to="/settings?tab=subscription"
              className="flex items-center gap-1 text-[11px] font-semibold transition-opacity hover:opacity-80"
              style={{ color: "#33b864" }}
            >
              <Zap className="h-3 w-3" />
              Upgrade
            </Link>
          )}
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-[11px]" style={{ color: "rgba(252,251,244,0.5)" }}>
              <FileText className="w-3 h-3" style={{ color: canCreate ? "rgba(252,251,244,0.5)" : "#ef4444" }} />
              Policies
            </span>
            <span
              className="text-[11px] font-semibold tabular-nums"
              style={{ color: !canCreate ? "#ef4444" : percentage >= 75 ? "#f59e0b" : "rgba(252,251,244,0.7)" }}
            >
              {used}/{total}
            </span>
          </div>
          {/* Custom progress bar */}
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(252,251,244,0.1)" }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${percentage}%`, background: barColor }}
            />
          </div>
        </div>
        {!canCreate && (
          <p className="text-[11px]" style={{ color: "#ef4444" }}>
            Limit reached.{" "}
            <Link
              to="/settings?tab=subscription"
              className="underline font-semibold"
              style={{ color: "#ef4444" }}
            >
              Upgrade
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
